/* eslint-disable */
import React, { useMemo, useState, useEffect } from "react";
import { compose } from "redux";
import { gql } from "apollo-boost";
import snakeCase from "lodash/snakeCase";
import startCase from "lodash/startCase";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useApolloClient } from "@apollo/react-hooks";
import {
  withStyles,
  withBreakpoint,
  withMobile,
  Dropdown
} from "components/common";
import Table from "components/common/Table";
import TableFilter from "components/common/TableFilter";
import CustomHeader from "components/common/Table/CustomHeader";
import CustomTableHeader from "components/common/Table/CustomTableHeader";
import CreatableSelect from "react-select/creatable";

import {
  setFilteredUsers,
  setCounts,
  setPage,
  setFilterBy,
  setSearchBy,
  setSortBy,
  setOrderBy,
  setFilterByReason,
  setFilterByAdditionalAccounts,
  setIncludeTags,
  setExcludeTags,
  setFilterByManualReview
} from "state/actions";
import { COMPLETION_STATUS } from "utils/constants";

const moment = require("moment");

const supportalTags = [
  "potential_fraud",
  "fraud_victim_init",
  "bankruptcy_ch7",
  "fraud_victim_ext",
  "security_freeze",
  "bankruptcy_ch13",
  "review_kyc",
  "low_average_balance",
  "shipping_address_warning",
  "email_not_verified",
  "multiple_ssn",
  "first_name_mismatch",
  "synthetic_fraud",
  "short_account_history",
  "review_overdraft",
  "phone_not_verified",
  "shipping_address_business",
  "risky_customer",
  "risky_application",
  "address_blacklisted",
  "bankruptcy_ch11",
  "billing_address_vacant",
  "shipping_address_cmra",
  "shipping_address_vacant",
  "ssn_not_verified",
  "compromised_q6",
  "confirmed_fraud",
  "charged-off"
]
  .sort()
  .map((item, idx) => ({
    value: item,
    label: startCase(item)
  }));

const manualReviewExcludedTags = [
  "bankruptcy_ch7",
  "bankruptcy_ch11",
  "bankruptcy_ch13",
  "security_freeze",
  "fraud_victim_init",
  "fraud_victim_ext",
  "synthetic_fraud",
  "multiple_ssn",
  "ssn_not_verified",
  "for_future_products"
];

const computeColumns = belowMd =>
  belowMd
    ? [
        {
          Header: "Name",
          accessor: data => `${data.first_name} ${data.last_name}`,
          id: "name"
        },
        {
          Header: "Status",
          accessor: "completion_status",
          id: "completion_status"
        }
      ]
    : [
        {
          Header: "Name",
          id: "name",
          accessor: data => `${data.first_name} ${data.last_name}`
        },
        {
          Header: "Email",
          id: "email",
          accessor: data =>
            data.email +
            (data.referrers && data.referrers.length
              ? `\n${data.referrers[0].sender_email}`
              : ""),
          Cell: ({ value }) => (
            <div className="flex-1 ml-1 md:ml-0">
              <div>{value.split("\n")[0]}</div>
              <div className="text-gray-500 text-sm md:text-sm">
                {!!value.split("\n")[1] &&
                  `referred by: ${value.split("\n")[1]}`}
              </div>
            </div>
          )
        },
        {
          Header: "Last update",
          accessor: "last_update",
          id: "last_update",
          Cell: ({ value }) =>
            moment.unix(value / 1000).format("M/D/YYYY (h:mma)")
        },
        {
          Header: "Current step",
          id: "current_step",
          accessor: data => data.current_step || "n/a"
        },
        {
          Header: "Status",
          id: "completion_status",
          accessor: data => data.completion_status
        },
        {
          id: "phone_number",
          Header: "Phone Number",
          accessor: data => data.phone_number
        }
      ];

const itemsPerPage = 20;
const orderConfig = {
  [-1]: "DESC",
  0: "",
  1: "ASC"
};

const checkForDuplicates = items => {
  const uniqueItems = items.reduce(
    (acc, curr, i) => {
      if (acc.set.has(curr.email)) {
        console.log("i", i);
        return acc;
      }
      return {
        set: acc.set.add(curr.email),
        undupedAccounts: [...acc.undupedAccounts, curr]
      };
    },
    {
      set: new Set(),
      undupedAccounts: []
    }
  );
  return uniqueItems.undupedAccounts;
};

const filterByReasonConfig = [
  "All",
  "Asset Report Timeout",
  "Average Low Balances",
  "Account Age",
  "KYC",
  "Overdraft Manual Review",
  "Overdraft Rejection",
  "Overdraft High Balance Exemption",
  "Bank Accounts Unlinked",
  "High Balance",
  "Duplicate Phone Number",
  "Duplicate Address",
  "Internal Error",
  "Duplicate IP",
  "Duplicate Bank",
  "For Future Products",
  "Sentilink",
  "Different Shipping Address",
  "Multiple SSN",
  "Cannot Verify SSN",
  "No ACH Account"
];

const CustomerSupportTable = ({
  // loading,
  breakpointContext: { isBelow }
}) => {
  const columns = useMemo(() => computeColumns(isBelow("md")), [isBelow("md")]);
  const client = useApolloClient();
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    filteredUsers,
    counts,
    page,
    filterBy,
    searchBy,
    sortBy,
    orderBy,
    filterByReason,
    filterByAdditionalAccounts,
    includeTags = [],
    excludeTags = [],
    filterByManualReview = false
  } = useSelector(state => state.supportal);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDemUsers = async () => {
      console.log("filtered users", filteredUsers);
      if (!filteredUsers.length) {
        console.log("querying this....");
        const results = await client.query({
          query: PORTAL_QUERY,
          variables: {
            page: 0,
            perPage: itemsPerPage,
            searchBy,
            filterBy,
            sortBy,
            orderBy: orderConfig[orderBy],
            shouldCount: true,
            filterByReason,
            filterByAdditionalAccounts,
            includeTags: JSON.stringify(includeTags.map(t => t.value)),
            excludeTags: JSON.stringify(excludeTags.map(t => t.value))
          }
        });
        dispatch(setFilteredUsers(results.data.portalUsers.portalUsers));
        dispatch(setCounts(results.data.portalUsers.summary));
        setLoading(false);
      }
    };
    loadDemUsers();
  }, []);

  const getSortByResults = async column => {
    dispatch(setPage(0));

    if (column.id === sortBy) {
      let newOrderBy;
      let newSortBy;
      switch (orderBy) {
        case -1: {
          newOrderBy = 0;
          newSortBy = "";
          break;
        }
        case 0: {
          newOrderBy = 1;
          newSortBy = column.id;
          break;
        }
        case 1: {
          newOrderBy = -1;
          newSortBy = column.id;
          break;
        }
      }
      dispatch(setOrderBy(newOrderBy));
      dispatch(setSortBy(newSortBy));
      const results = await client.query({
        query: PORTAL_QUERY,
        variables: {
          page: 0,
          sortBy: newSortBy,
          orderBy: orderConfig[newOrderBy],
          perPage: itemsPerPage,
          searchBy,
          filterBy,
          filterByReason,
          filterByAdditionalAccounts,
          includeTags: JSON.stringify(includeTags.map(t => t.value)),
          excludeTags: JSON.stringify(excludeTags.map(t => t.value))
        }
      });
      dispatch(setFilteredUsers(results.data.portalUsers.portalUsers));
    } else {
      const newOrderBy = 1;
      const newSortBy = column.id;
      dispatch(setOrderBy(newOrderBy));
      dispatch(setSortBy(newSortBy));
      const results = await client.query({
        query: PORTAL_QUERY,
        variables: {
          page: 0,
          sortBy: newSortBy,
          orderBy: orderConfig[newOrderBy],
          perPage: itemsPerPage,
          searchBy,
          filterBy,
          filterByReason,
          filterByAdditionalAccounts,
          includeTags: JSON.stringify(includeTags.map(t => t.value)),
          excludeTags: JSON.stringify(excludeTags.map(t => t.value))
        }
      });
      dispatch(setFilteredUsers(results.data.portalUsers.portalUsers));
    }
  };

  const getSearchByResults = async newSearchBy => {
    dispatch(setSearchBy(newSearchBy));
    const results = await client.query({
      query: PORTAL_QUERY,
      variables: {
        page: 0,
        perPage: itemsPerPage,
        searchBy: newSearchBy,
        filterBy: filterBy,
        sortBy,
        orderBy: orderConfig[orderBy],
        shouldCount: true,
        filterByReason,
        filterByAdditionalAccounts,
        includeTags: JSON.stringify(includeTags.map(t => t.value)),
        excludeTags: JSON.stringify(excludeTags.map(t => t.value))
      }
    });
    dispatch(setPage(0));
    dispatch(setFilteredUsers(results.data.portalUsers.portalUsers));
    dispatch(setCounts(results.data.portalUsers.summary));
  };

  const getNewPageResults = async newPage => {
    console.log("new page", newPage);
    dispatch(setPage(newPage));

    const results = await client.query({
      query: PORTAL_QUERY,
      variables: {
        page: newPage,
        perPage: itemsPerPage,
        searchBy,
        filterBy,
        sortBy,
        orderBy: orderConfig[orderBy],
        filterByReason,
        filterByAdditionalAccounts,
        includeTags: JSON.stringify(includeTags.map(t => t.value)),
        excludeTags: JSON.stringify(excludeTags.map(t => t.value))
      }
    });
    dispatch(
      setFilteredUsers(
        checkForDuplicates([
          ...filteredUsers,
          ...results.data.portalUsers.portalUsers
        ])
      )
    );
  };

  const getFilterByResults = async newFilterBy => {
    if (newFilterBy !== filterBy) {
      console.log("updating...");
      dispatch(setPage(0));
      setLoading(true);
      dispatch(setFilterBy(newFilterBy));
      const results = await client.query({
        query: PORTAL_QUERY,
        variables: {
          page: 0,
          perPage: itemsPerPage,
          searchBy,
          filterBy: newFilterBy,
          sortBy,
          orderBy: orderConfig[orderBy],
          filterByReason,
          filterByAdditionalAccounts,
          includeTags: JSON.stringify(includeTags.map(t => t.value)),
          excludeTags: JSON.stringify(excludeTags.map(t => t.value))
        }
      });
      console.log("apply filter results", results);
      setLoading(false);
      dispatch(setFilteredUsers(results.data.portalUsers.portalUsers));
      // setCounts(results.data.portalUsers.summary);
    }
  };

  const getFilterByReasonResults = async newFilterByReason => {
    console.log("newFilterReason", newFilterByReason);
    if (newFilterByReason !== filterByReason) {
      dispatch(setPage(0));
      setLoading(true);
      dispatch(setFilterByReason(newFilterByReason));
      const results = await client.query({
        query: PORTAL_QUERY,
        variables: {
          page: 0,
          perPage: itemsPerPage,
          searchBy,
          filterBy: "",
          sortBy,
          orderBy: orderConfig[orderBy],
          filterByReason: newFilterByReason,
          shouldCount: true,
          filterByAdditionalAccounts,
          includeTags: JSON.stringify(includeTags.map(t => t.value)),
          excludeTags: JSON.stringify(excludeTags.map(t => t.value))
        }
      });
      console.log("apply filter results", results);
      setLoading(false);
      dispatch(setFilteredUsers(results.data.portalUsers.portalUsers));
      dispatch(setCounts(results.data.portalUsers.summary));
    }
  };

  const getFilterByAdditionalAccounts = async newFilterByAdditionalAccounts => {
    if (newFilterByAdditionalAccounts !== filterByAdditionalAccounts) {
      dispatch(setPage(0));
      setLoading(true);
      dispatch(setFilterByAdditionalAccounts(newFilterByAdditionalAccounts));
      const results = await client.query({
        query: PORTAL_QUERY,
        variables: {
          page: 0,
          perPage: itemsPerPage,
          searchBy,
          filterBy: "",
          sortBy,
          orderBy: orderConfig[orderBy],
          filterByReason,
          shouldCount: true,
          filterByAdditionalAccounts: newFilterByAdditionalAccounts,
          includeTags: JSON.stringify(includeTags.map(t => t.value)),
          excludeTags: JSON.stringify(excludeTags.map(t => t.value))
        }
      });
      setLoading(false);
      dispatch(setFilteredUsers(results.data.portalUsers.portalUsers));
      dispatch(setCounts(results.data.portalUsers.summary));
    }
  };

  const getManualReviewUsers = async newFilterByManualReview => {
    let newTagsToExclude = [];
    let newFilterBy = "";
    if (newFilterByManualReview) {
      newTagsToExclude = [
        ...excludeTags.map(t => t.value),
        ...manualReviewExcludedTags
      ];
      newFilterBy = "pending";
    }

    const results = await client.query({
      query: PORTAL_QUERY,
      variables: {
        page: 0,
        perPage: itemsPerPage,
        searchBy,
        filterBy: newFilterBy,
        sortBy,
        orderBy: orderConfig[orderBy],
        shouldCount: true,
        filterByReason,
        filterByAdditionalAccounts,
        includeTags: JSON.stringify(includeTags.map(t => t.value)),
        excludeTags: JSON.stringify(newTagsToExclude)
      }
    });
    dispatch(setPage(0));
    dispatch(setFilterBy(newFilterBy));
    dispatch(
      setExcludeTags(newTagsToExclude.map(t => ({ label: t, value: t })))
    );
    dispatch(setFilterByManualReview(newFilterByManualReview));
    dispatch(setFilteredUsers(results.data.portalUsers.portalUsers));
    dispatch(setCounts(results.data.portalUsers.summary));
  };

  const mapping = {
    all: {
      name: `All (${counts.total_count})`,
      fn: () => getFilterByResults("")
    },
    not_started: {
      name: `Not Started (${counts.not_started_count})`,
      fn: () => getFilterByResults(COMPLETION_STATUS.NOT_STARTED)
    },
    started: {
      name: `Started (${counts.started_count})`,
      fn: () => getFilterByResults(COMPLETION_STATUS.STARTED)
    },
    approved: {
      name: `Approved (${counts.approved_count})`,
      fn: () => getFilterByResults(COMPLETION_STATUS.APPROVED)
    },
    pending: {
      name: `Pending (${counts.pending_count})`,
      fn: () => getFilterByResults(COMPLETION_STATUS.PENDING)
    },
    rejected: {
      name: `Rejected (${counts.rejected_count})`,
      fn: () => getFilterByResults(COMPLETION_STATUS.REJECTED)
    },
    awaiting_user_response: {
      name: `Awaiting User Response (${counts.awaiting_user_response_count})`,
      fn: () => getFilterByResults(COMPLETION_STATUS.AWAITING_USER_RESPONSE)
    }
  };

  const getRowStyle = row => {
    return {
      backgroundColor:
        row.original.completion_status === COMPLETION_STATUS.REJECTED ? "#FFE4E5" : "#E5FFE5"
    };
  };

  // const sendUpdate = (row, updates) => {
  //   setAllUsers(
  //     allUsers.map(user =>
  //       user.email == row.original.email
  //         ? {
  //             ...user,
  //             ...updates
  //           }
  //         : user
  //     )
  //   );
  //   setUpdatedUser(row);
  // };

  console.log("startCase....", filterByReason, startCase(filterByReason));
  return (
    <div className="mt-5">
      <div className="float-right mr-4">
        <a
          className="underline hover:text-green-primary"
          href="/dashboard/admin/god"
        >
          GodView
        </a>
      </div>
      <h1 className="text-2xl font-title mb-2 font-light">
        The Glorious Standalone Customer Supportal
      </h1>
      <div className="flex">
        <TableFilter
          mapping={mapping}
          containerClassName="flex-initial text-gray-primary font-light"
          initialActive={filterBy || "all"}
        />
      </div>

      <div className="my-6">
        <Table
          id="referrals-table"
          columns={columns}
          data={filteredUsers.slice(
            page * itemsPerPage,
            page * itemsPerPage + itemsPerPage
          )}
          onRowClick={(e, row) => {
            history.push(`/supportal/user?email=${encodeURIComponent(row.original.email)}`);
          }}
          getRowStyle={getRowStyle}
          itemsPerPage={20}
          defaultSortOrder={{ idx: 2, order: true }}
          HeaderComponent={() => (
            <div>
              <div className="flex">
                <CustomHeader
                  nextPage={() => getNewPageResults(page + 1)}
                  canNextPage={(() => {
                    const currentFilter = filterBy === "" ? "total" : filterBy;
                    const currentCount = counts[`${currentFilter}_count`];
                    const totalItems = (page + 1) * itemsPerPage;
                    return totalItems < currentCount;
                  })()}
                  previousPage={() => dispatch(setPage(page - 1))}
                  canPreviousPage={(() => {
                    return page > 0;
                  })()}
                  gotoPage={page => getNewPageResults(page)}
                  pageCount={(() => {
                    const currentFilter = filterBy === "" ? "total" : filterBy;
                    const currentCount = counts[`${currentFilter}_count`];
                    return Math.ceil(currentCount / itemsPerPage);
                  })()}
                  pageIndex={page}
                  doSearch={searchQuery => getSearchByResults(searchQuery)}
                  initialSearchQuery={searchBy}
                  showGotoPage={false}
                  showSuperJump={false}
                />
                <div className="flex" style={{ width: 200 }}>
                  <Dropdown
                    selectedElement={{
                      name: startCase(filterByReason),
                      value: filterByReason
                    }}
                    elements={filterByReasonConfig
                      .sort((a, b) => {
                        if (a == "All") return -1;
                        if (a < b) return -1;
                        return 1;
                      })
                      .map(item => ({
                        name: item,
                        value: snakeCase(item)
                      }))}
                    didSelect={item => {
                      getFilterByReasonResults(item.value);
                      console.log(item);
                    }}
                    leftAligned
                    border
                  />
                </div>
                <div className="flex items-center ml-auto">
                  <label htmlFor="accounts" className="pr-1">
                    Added additional accounts
                  </label>
                  <input
                    type="checkbox"
                    className="align-middle mr-2"
                    id="accounts"
                    // defaultChecked={ignoreKyc}
                    checked={filterByAdditionalAccounts}
                    onChange={() => {
                      getFilterByAdditionalAccounts(
                        !filterByAdditionalAccounts
                      );
                    }}
                  />
                </div>
              </div>
              <div key="tag-filter" className="flex my-2">
                <span className="flex-1 mr-2">
                  <CreatableSelect
                    placeholder="Include tags..."
                    name="supportal-inctags"
                    isMulti={true}
                    options={supportalTags}
                    value={includeTags}
                    onChange={(newValue, actionMeta) =>
                      dispatch(setIncludeTags(newValue))
                    }
                  />
                </span>
                <span className="flex-1 mr-2">
                  <CreatableSelect
                    placeholder="Exclude tags..."
                    name="supportal-exctags"
                    isMulti={true}
                    options={supportalTags}
                    value={excludeTags}
                    onChange={(newValue, actionMeta) =>
                      dispatch(setExcludeTags(newValue))
                    }
                  />
                </span>

                <button
                  type="button"
                  className={`btn py-0 h-10 w-32 border-0 cursor-pointer text-sm ${
                    filterByManualReview ? "bg-gray-400" : "btn-green"
                  }`}
                  onClick={() => getManualReviewUsers(!filterByManualReview)}
                >
                  {filterByManualReview ? "Reset" : "Manual Review"}
                </button>
              </div>
            </div>
          )}
          TableHeaderComponent={CustomTableHeader({
            handleSort: getSortByResults,
            orderBy,
            sortBy
          })}
        />
      </div>
    </div>
  );
};

const PORTAL_QUERY = gql`
  query Portal(
    $searchBy: String
    $page: Int
    $perPage: Int
    $filterBy: String
    $sortBy: String
    $orderBy: String
    $shouldCount: Boolean
    $filterByReason: String
    $filterByAdditionalAccounts: Boolean
    $includeTags: String
    $excludeTags: String
  ) {
    portalUsers(
      searchBy: $searchBy
      page: $page
      perPage: $perPage
      filterBy: $filterBy
      sortBy: $sortBy
      orderBy: $orderBy
      shouldCount: $shouldCount
      filterByReason: $filterByReason
      filterByAdditionalAccounts: $filterByAdditionalAccounts
      includeTags: $includeTags
      excludeTags: $excludeTags
    ) {
      portalUsers {
        first_name
        last_name
        last_update
        last_completed_step
        phone_number
        email
        current_step
        completion_status
      }
      summary {
        total_count
        not_started_count
        started_count
        pending_count
        rejected_count
        approved_count
        awaiting_user_response_count
      }
    }
  }
`;

export default compose(
  withMobile,
  withStyles,
  withBreakpoint
)(CustomerSupportTable);
